import React, { useState, useEffect } from "react";
import { toastSuccess, toastError } from "../utils/toast";
import { z } from "zod";
import { companyService } from "../services/api";
import {
  Table,
  FormPopup,
  DeletePopUp,
  EditIcon,
  DeleteIcon,
} from "../components/common";
import { ContainedSelect } from "../components/common/ContainedSelect";
import { INDIA_STATES, getCitiesForState, isCityInState } from "../utils/indiaStateCity";

const companyFormSchema = z.object({
  companyCode: z.string().optional(),
  name: z.string().min(1, "Company name is required"),
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
  phone: z.string().min(1, "Phone is required"),
  gstin: z.string().optional(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().min(1, "City is required"),
      state: z.string().min(1, "State is required"),
      pincode: z.string().optional(),
      country: z.string().optional(),
    })
    .optional(),
});

const COMPANY_COLUMNS = [
  { key: "companyCode", label: "Code" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  {
    key: "address",
    label: "Address",
    render: (_, row) => {
      const a = row.address;
      if (!a) return "—";
      const parts = [a.line1, a.line2, a.city, a.state, a.pincode].filter(
        Boolean
      );
      return parts.length ? parts.join(", ") : "—";
    },
  },
];

export function CompanyManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyCode: "",
    name: "",
    email: "",
    phone: "",
    gstin: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    secondaryCustomers: [],
  });
  const [currentSecondaryCustomer, setCurrentSecondaryCustomer] = useState({
    customerCode: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerGstin: "",
    customerAddress: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
  });
  const [showSecondaryCustomerInputs, setShowSecondaryCustomerInputs] =
    useState(false);
  const [addressCityMismatch, setAddressCityMismatch] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchCompanies(), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      const result = await companyService.getCompanies(params);
      setCompanies(result.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      companyCode: "",
      name: "",
      email: "",
      phone: "",
      gstin: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
      secondaryCustomers: [],
    });
    setCurrentSecondaryCustomer({
      customerCode: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerGstin: "",
      customerAddress: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
    setShowSecondaryCustomerInputs(false);
    setAddressCityMismatch(false);
    setEditingCompanyId(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const dataToSubmit = e && e.preventDefault ? formData : e;

    const parsed = companyFormSchema.safeParse({
      ...dataToSubmit,
      address: dataToSubmit.address || {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      const msg = firstIssue?.message || "Validation failed";
      toastError(msg);
      return;
    }

    try {
      if (editingCompanyId) {
        await companyService.updateCompany(editingCompanyId, dataToSubmit);
        toastSuccess("Company updated successfully");
      } else {
        await companyService.createCompany(dataToSubmit);
        toastSuccess("Company created successfully");
      }
      resetForm();
      setShowForm(false);
      fetchCompanies();
    } catch (error) {
      console.error("Error saving company:", error);
      toastError(error);
    }
  };

  const handleEdit = (row) => {
    const state = row.address?.state?.trim() || "";
    const city = row.address?.city?.trim() || "";
    const cityValidForState = state && city && isCityInState(city, state);
    setFormData({
      companyCode: row.companyCode || "",
      name: row.name || "",
      email: row.email || "",
      phone: row.phone || "",
      gstin: row.gstin || "",
      address: {
        line1: row.address?.line1 || "",
        line2: row.address?.line2 || "",
        city: cityValidForState ? city : "",
        state,
        pincode: row.address?.pincode || "",
        country: row.address?.country || "India",
      },
      secondaryCustomers: row.secondaryCustomers || [],
    });
    setAddressCityMismatch(!!(state && city && !cityValidForState));
    setCurrentSecondaryCustomer({
      customerCode: "",
      customerName: "",
    });
    setShowSecondaryCustomerInputs(false);
    setEditingCompanyId(row._id || row.id);
    setShowForm(true);
  };
  const updateSecondaryCustomerField = (index, field, value) => {
    const updated = [...(formData.secondaryCustomers || [])];
    updated[index] = { ...(updated[index] || {}), [field]: value };
    setFormData({ ...formData, secondaryCustomers: updated });
  };

  const updateSecondaryCustomerAddressField = (index, field, value) => {
    const updated = [...(formData.secondaryCustomers || [])];
    const prev = updated[index] || {};
    const prevAddr = prev.customerAddress || {};
    updated[index] = {
      ...prev,
      customerAddress: {
        ...prevAddr,
        [field]: value,
        country: prevAddr.country || "India",
      },
    };
    setFormData({ ...formData, secondaryCustomers: updated });
  };
  const handleAddSecondaryCustomer = () => {
    const code = currentSecondaryCustomer.customerCode?.trim() || "";
    const name = currentSecondaryCustomer.customerName?.trim() || "";
    const email =
      currentSecondaryCustomer.customerEmail?.trim()?.toLowerCase() || "";
    const phone = currentSecondaryCustomer.customerPhone?.trim() || "";
    const gstin =
      currentSecondaryCustomer.customerGstin?.trim()?.toUpperCase() || "";

    if (!code) {
      toastError("Customer Code is required");
      return;
    }

    if (!name) {
      toastError("Customer Name is required");
      return;
    }

    const a = currentSecondaryCustomer.customerAddress || {};
    const state = a.state?.trim() || "";
    const city = a.city?.trim() || "";
    if (!state) {
      toastError("State is required for Secondary Customer address");
      return;
    }
    if (!city) {
      toastError("City is required for Secondary Customer address");
      return;
    }

    const existing = formData.secondaryCustomers || [];
    const codeExists = existing.some((c) => c.customerCode === code);
    const nameExists = existing.some(
      (c) => c.customerName.toLowerCase() === name.toLowerCase()
    );

    if (codeExists) {
      toastError(`Customer Code "${code}" already exists`);
      return;
    }

    if (nameExists) {
      toastError(`Customer Name "${name}" already exists`);
      return;
    }
    const customerAddress = {
      line1: a.line1?.trim() || "",
      line2: a.line2?.trim() || "",
      city: a.city?.trim() || "",
      state: a.state?.trim() || "",
      pincode: a.pincode?.trim() || "",
      country: a.country?.trim() || "India",
    };
    setFormData({
      ...formData,
      secondaryCustomers: [
        ...existing,
        {
          customerCode: code,
          customerName: name,
          customerEmail: email || undefined,
          customerPhone: phone || undefined,
          customerGstin: gstin || undefined,
          customerAddress,
          isActive: true,
        },
      ],
    });

    setCurrentSecondaryCustomer({
      customerCode: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerGstin: "",
      customerAddress: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
    });
    setShowSecondaryCustomerInputs(false);
  };

  const handleRemoveSecondaryCustomer = (index) => {
    const updated = (formData.secondaryCustomers || []).filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      secondaryCustomers: updated,
    });
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      await companyService.deleteCompany(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      fetchCompanies();
      toastSuccess("Company deactivated successfully");
    } catch (err) {
      console.error("Error deleting company:", err);
      toastError(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const companyActions = [
    {
      label: "Edit",
      icon: EditIcon,
      onClick: handleEdit,
    },
    {
      label: "Delete",
      icon: DeleteIcon,
      onClick: handleDelete,
    },
  ];

  const companyFields = [
    {
      name: "companyCode",
      label: "Primary Customer Code",
      type: "text",
      required: false,
      grid: "1",
      disabled: !!editingCompanyId,
      placeholder: "Optional – leave blank to auto-generate",
    },
    {
      name: "name",
      label: "Primary Customer Name",
      type: "text",
      required: true,
      grid: "1",
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      required: true,
      grid: "1",
    },
    {
      name: "phone",
      label: "Phone",
      type: "text",
      required: true,
      grid: "1",
    },
    {
      name: "gstin",
      label: "GSTIN Number",
      type: "text",
      grid: "1",
    },
    {
      name: "address.line1",
      label: "Address Line 1",
      type: "text",
      grid: "1",
    },
    {
      name: "address.line2",
      label: "Address Line 2",
      type: "text",
      grid: "1",
    },
    {
      type: "custom",
      grid: "2",
      render: () => {
        const state = formData.address?.state ?? "";
        const city = formData.address?.city ?? "";
        const cityOptions = getCitiesForState(state);
        const cityDisabled = !state;
        return (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label className="form-popup-label mb-1.5 block">
                State <span className="ml-1 text-red-500">*</span>
              </label>
              <ContainedSelect
                value={state}
                onChange={(e) => {
                  const newState = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    address: {
                      ...(prev.address || {}),
                      state: newState,
                      city: "",
                      line1: prev.address?.line1,
                      line2: prev.address?.line2,
                      pincode: prev.address?.pincode,
                      country: prev.address?.country || "India",
                    },
                  }));
                  setAddressCityMismatch(false);
                }}
                options={INDIA_STATES}
                placeholder="Select State"
              />
            </div>
            <div>
              <label className="form-popup-label mb-1.5 block">
                City <span className="ml-1 text-red-500">*</span>
              </label>
              <ContainedSelect
                value={city}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    address: {
                      ...(prev.address || {}),
                      city: e.target.value,
                      state: prev.address?.state,
                      line1: prev.address?.line1,
                      line2: prev.address?.line2,
                      pincode: prev.address?.pincode,
                      country: prev.address?.country || "India",
                    },
                  }));
                  setAddressCityMismatch(false);
                }}
                options={cityOptions}
                placeholder="Select City"
                disabled={cityDisabled}
              />
              {addressCityMismatch && (
                <p className="mt-1.5 text-sm text-red-500">Saved city does not belong to selected state. Please select a city.</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      name: "address.pincode",
      label: "Pincode",
      type: "text",
      grid: "1",
    },
    {
      type: "custom",
      grid: "full",
      render: () =>
        !editingCompanyId && (
          <div
            className="secWrap"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 18,
            }}
          >
            <div style={{ alignSelf: "flex-start" }}>
              <button
                type="button"
                onClick={() =>
                  setShowSecondaryCustomerInputs(!showSecondaryCustomerInputs)
                }
                className="w-fit h-11 px-[18px] rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ height: 44 }}
              >
                Add Secondary Customer
              </button>
            </div>
            {showSecondaryCustomerInputs && (
              <div
                style={{
                  width: "100%",
                  display: "block",
                  border: "1px solid #E4E7EC",
                  borderRadius: 14,
                  padding: 18,
                  backgroundColor: "#FFFFFF",
                }}
              >
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#101828",
                marginBottom: 16,
              }}
            >
              Seconday Customer
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                columnGap: 26,
                rowGap: 18,
              }}
            >
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Customer Code
                </label>
                <input
                  type="text"
                  value={currentSecondaryCustomer.customerCode}
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerCode: e.target.value,
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter customer code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={currentSecondaryCustomer.customerName}
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerName: e.target.value,
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Customer Email
                </label>
                <input
                  type="email"
                  value={currentSecondaryCustomer.customerEmail}
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerEmail: e.target.value,
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Customer Phone
                </label>
                <input
                  type="text"
                  value={currentSecondaryCustomer.customerPhone}
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerPhone: e.target.value,
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Customer GSTIN
                </label>
                <input
                  type="text"
                  value={currentSecondaryCustomer.customerGstin}
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerGstin: e.target.value,
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter GSTIN"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={
                    currentSecondaryCustomer.customerAddress?.line1 || ""
                  }
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerAddress: {
                        ...(currentSecondaryCustomer.customerAddress || {}),
                        line1: e.target.value,
                        country:
                          currentSecondaryCustomer.customerAddress?.country ||
                          "India",
                      },
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter address line 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={
                    currentSecondaryCustomer.customerAddress?.line2 || ""
                  }
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerAddress: {
                        ...(currentSecondaryCustomer.customerAddress || {}),
                        line2: e.target.value,
                        country:
                          currentSecondaryCustomer.customerAddress?.country ||
                          "India",
                      },
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter address line 2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <ContainedSelect
                  value={currentSecondaryCustomer.customerAddress?.state || ""}
                  onChange={(e) => {
                    const newState = e.target.value;
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerAddress: {
                        ...(currentSecondaryCustomer.customerAddress || {}),
                        state: newState,
                        city: "",
                        country:
                          currentSecondaryCustomer.customerAddress?.country ||
                          "India",
                      },
                    });
                  }}
                  options={INDIA_STATES}
                  placeholder="Select State"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <ContainedSelect
                  value={currentSecondaryCustomer.customerAddress?.city || ""}
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerAddress: {
                        ...(currentSecondaryCustomer.customerAddress || {}),
                        city: e.target.value,
                        state:
                          currentSecondaryCustomer.customerAddress?.state,
                        country:
                          currentSecondaryCustomer.customerAddress?.country ||
                          "India",
                      },
                    })
                  }
                  options={getCitiesForState(currentSecondaryCustomer.customerAddress?.state || "")}
                  placeholder="Select City"
                  disabled={!currentSecondaryCustomer.customerAddress?.state}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#344054] mb-2">
                  Pincode
                </label>
                <input
                  type="text"
                  value={
                    currentSecondaryCustomer.customerAddress?.pincode || ""
                  }
                  onChange={(e) =>
                    setCurrentSecondaryCustomer({
                      ...currentSecondaryCustomer,
                      customerAddress: {
                        ...(currentSecondaryCustomer.customerAddress || {}),
                        pincode: e.target.value,
                        country:
                          currentSecondaryCustomer.customerAddress?.country ||
                          "India",
                      },
                    })
                  }
                  className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm text-gray-800 placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                  style={{ height: 44, padding: "0 14px" }}
                  placeholder="Enter pincode"
                />
              </div>
            </div>
            <div
              className="flex justify-end"
              style={{ gap: 16, marginTop: 18 }}
            >
              <button
                type="button"
                onClick={() => {
                  setShowSecondaryCustomerInputs(false);
                  setCurrentSecondaryCustomer({
                    customerCode: "",
                    customerName: "",
                    customerEmail: "",
                    customerPhone: "",
                    customerGstin: "",
                    customerAddress: {
                      line1: "",
                      line2: "",
                      city: "",
                      state: "",
                      pincode: "",
                      country: "India",
                    },
                  });
                }}
                className="rounded-[10px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold transition hover:bg-[#188742]/5"
                style={{ width: 200, height: 44 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddSecondaryCustomer}
                className="rounded-[10px] bg-[#188742] text-white font-semibold transition hover:bg-[#15733a]"
                style={{ width: 200, height: 44 }}
              >
                Add
              </button>
            </div>
          </div>
            )}
          </div>
        ),
    },
    {
      type: "custom",
      grid: "full",
      render: () =>
        formData.secondaryCustomers &&
        formData.secondaryCustomers.length > 0 && (
          <div
            className="mb-5"
            style={{
              border: "2px dashed rgba(24,135,66,0.55)",
              borderRadius: 14,
              backgroundColor: "rgba(24,135,66,0.04)",
              padding: 16,
            }}
          >
            <div
              className="mb-2"
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: "#344054",
              }}
            >
              Added Secondary Customers : {formData.secondaryCustomers.length}
            </div>
            <div className="overflow-x-auto rounded-[14px] border border-[#EEF1F5] bg-white p-[14px]">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                      Customer Code
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                      Customer Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formData.secondaryCustomers.map((customer, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {editingCompanyId ? (
                          <input
                            type="text"
                            value={customer.customerCode || ""}
                            disabled
                            className="h-10 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700"
                          />
                        ) : (
                          customer.customerCode
                        )}
                      </td>

                      <td className="px-4 py-2 text-sm text-gray-700">
                        {editingCompanyId ? (
                          <input
                            type="text"
                            value={customer.customerName || ""}
                            onChange={(e) =>
                              updateSecondaryCustomerField(
                                index,
                                "customerName",
                                e.target.value
                              )
                            }
                            className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          />
                        ) : (
                          customer.customerName
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveSecondaryCustomer(index)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium transition"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
    },
  ];

  return (
    <div className="page-container">
      {/* ✅ Header (same as screenshot) */}
      <div className="mb-6">
        <div className="rounded-[28px] border border-gray-100 bg-white px-6 py-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Left: Title */}
            <div className="min-w-0">
              <h1 className="whitespace-nowrap text-lg font-medium text-gray-900">
                Company Management
              </h1>
              {/* ✅ subheading removed */}
            </div>

            {/* Right: Search + Button */}
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
              {/* Search */}
              <div className="w-full sm:w-[320px]">
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {/* search icon */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by ID"
                    className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 placeholder:text-gray-400 shadow-sm focus:border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              </div>

              {/* Button */}
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="h-11 shrink-0 rounded-xl bg-emerald-700 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 active:scale-[0.98]"
              >
                Create New Company
              </button>
            </div>
          </div>
        </div>
      </div>

      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => {
          setDeletePopUpOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        entityLabel="company"
      />
      <FormPopup
        open={showForm}
        title={editingCompanyId ? "Edit Company" : "Add New Company"}
        onClose={() => {
          setShowForm(false);
          resetForm();
        }}
        onSubmit={handleSubmit}
        submitText={editingCompanyId ? "Update" : "Create"}
        values={formData}
        setValues={setFormData}
        fields={companyFields}
        width="xl"
      />

      <Table
        columns={COMPANY_COLUMNS}
        data={companies}
        loading={loading}
        emptyMessage="No companies found"
        actions={companyActions}
      />
    </div>
  );
}
